import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { GameCover } from '@/components/ui/GameCover'
import { Spinner } from '@/components/ui/Spinner'
import { fetchLibrary, refreshLibrary } from '@/api/users'
import { hueFromId } from '@/lib/steam'
import { useSyncStore } from '@/store/sync'
import type { Game, OwnedGame } from '@/types'

function ownedGameToGameShape(g: OwnedGame): Game {
  const hue = hueFromId(g.appId)
  return {
    id: g.appId,
    appId: parseInt(g.appId, 10),
    title: g.name,
    hue,
    accent: (hue + 40) % 360,
    earlyAccess: false,
    tags: [],
    price: 0,
    sale: null,
    currency: 'CAD',
    rating: 0,
    reviews: 0,
    released: '',
    developer: '',
    publisher: '',
    desc: '',
    multiplayer: '',
    coop: null,
    headerImageUrl: g.headerImageUrl || undefined,
  }
}

function relativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diffMs / 60_000)
  if (mins < 60) return mins <= 1 ? 'just now' : `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.floor(hours / 24)}d ago`
}

export default function LibraryPage() {
  const queryClient = useQueryClient()
  const isSyncing = useSyncStore((s) => s.isSyncing)

  const { data: library, isLoading, isError } = useQuery({
    queryKey: ['library'],
    queryFn: fetchLibrary,
  })

  const { mutate: doRefresh, isPending: refreshing } = useMutation({
    mutationFn: refreshLibrary,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['library'] }),
  })

  const sortedGames = library ? [...library.games].sort((a, b) => a.name.localeCompare(b.name)) : []

  return (
    <div className="cg-screen">
      <div className="cg-page-header">
        <div>
          <h1>Steam Library</h1>
          <p className="cg-page-sub">
            {library
              ? `${library.gameCount} games · last synced ${library.lastSyncedAt ? relativeTime(library.lastSyncedAt) : 'never'}`
              : 'Your owned Steam games'}
          </p>
        </div>
        <button
          className="cg-btn"
          onClick={() => doRefresh()}
          disabled={refreshing || isLoading || isSyncing}
        >
          {refreshing ? <span className="cg-spin">↻</span> : '↻'} Refresh
        </button>
      </div>

      {isLoading && <Spinner />}

      {isError && (
        <p style={{ color: 'var(--fg-3)', fontSize: '13px' }}>
          Could not load library. Try refreshing.
        </p>
      )}

      {library && library.games.length === 0 && library.lastSyncedAt !== null && (
        <p style={{ color: 'var(--fg-3)', fontSize: '13px' }}>
          No games found. Your library may be set to private on Steam.
        </p>
      )}

      {library && library.games.length === 0 && library.lastSyncedAt === null && (
        <p style={{ color: 'var(--fg-3)', fontSize: '13px' }}>
          Hit Refresh to sync your Steam library.
        </p>
      )}

      {sortedGames.length > 0 && (
        <div className="cg-library-grid">
          {sortedGames.map((g) => {
            const game = ownedGameToGameShape(g)
            return (
              <div key={g.appId} className="cg-lib-card">
                <GameCover game={game} size="md" />
                <div className="cg-lib-body">
                  <div style={{ fontWeight: 500, fontSize: '13px', lineHeight: 1.3 }}>
                    {g.name}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
